/* eslint-disable @blitz/block-scope-case */
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getBrowserInfo } from '~/utils/browser';
import { useLoaderData } from '@remix-run/react';
import { genTemplateGitHub } from '~/utils/templates';
import { chatStore } from '~/lib/stores/chat';
import { useStore } from '@nanostores/react';

type MenuLink = {
  type: 'link' | 'bug' | 'feature';
  icon: React.ReactNode;
  title: string;
  url: string;
  delay?: number;
};

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'flex';

interface HelpMenuProps {
  position?: Position;
  buttonClassName?: string;
  withBg?: boolean;
  framework?: string;
}

const HelpMenu: React.FC<HelpMenuProps> = ({
  position = 'bottom-right',
  buttonClassName = 'flex items-center justify-center w-10 h-10 rounded-full bg-alpha-background-overlay-light text-white shadow-lg transition-colors duration-200',
  withBg = false,
  framework,
}) => {
  const chat = useStore(chatStore);
  const { NEXT_PUBLIC_GITHUB_REPO } = useLoaderData<Env>();
  const [isOpen, setIsOpen] = useState(false);
  const [effectivePosition, setEffectivePosition] = useState<Exclude<Position, 'flex'>>(
    position === 'flex' ? 'top-left' : position,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (position === 'flex' && buttonRef.current && isOpen) {
      // Get button position and dimensions
      const rect = buttonRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Calculate available space in each direction
      const spaceAbove = rect.top;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceLeft = rect.left;
      const spaceRight = windowWidth - rect.right;

      // Determine best position - only for menu opening direction
      const verticalPosition = spaceBelow > spaceAbove ? 'top' : 'bottom';
      const horizontalPosition = spaceRight > spaceLeft ? 'left' : 'right';

      setEffectivePosition(`${verticalPosition}-${horizontalPosition}` as Exclude<Position, 'flex'>);
    } else if (position !== 'flex') {
      setEffectivePosition(position);
    }
  }, [isOpen, position]);

  const createGithubIssueUrl = (item: MenuLink) => {
    const baseUrl = `https://github.com/${NEXT_PUBLIC_GITHUB_REPO}/issues/new`;
    const frameworkLabels = framework?.toLowerCase().split('_');
    const bugLabels = [item.type === 'bug' ? 'bug' : 'enhancement', ...(frameworkLabels || [])];
    const enhancedBody = genTemplateGitHub({ type: item.type as 'bug' | 'feature', framework });
    const params = new URLSearchParams();

    params.append('title', item.title);
    params.append('body', enhancedBody);
    params.append('labels', bugLabels.join(','));

    return `${baseUrl}?${params.toString()}`;
  };

  const handleLinkClick = (link: MenuLink) => {
    if (link.type === 'bug' || link.type === 'feature') {
      window.open(createGithubIssueUrl(link), '_blank', 'noopener,noreferrer');
    } else {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Position-based styles - fixed positioning only for preset positions
  const menuContainerStyle =
    position === 'flex'
      ? { position: 'relative' as const } // Just make it relative for proper menu positioning
      : {
          position: 'fixed' as const,
          top: effectivePosition.includes('top') ? '1.5rem' : 'auto',
          bottom: effectivePosition.includes('bottom') ? '1.5rem' : 'auto',
          left: effectivePosition.includes('left') ? '1.5rem' : 'auto',
          right: effectivePosition.includes('right') ? '1.5rem' : 'auto',
        };

  // Animation values based on position
  const getAnimationValues = () => {
    switch (effectivePosition) {
      case 'top-left':
        return {
          initial: { opacity: 0, y: -20, x: -20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: -20, x: -20 },
          menuClassName: 'absolute top-full left-0 flex flex-col gap-2 mt-3',
          itemAnimation: { x: -20 },
        };
      case 'top-right':
        return {
          initial: { opacity: 0, y: -20, x: 20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: -20, x: 20 },
          menuClassName: 'absolute top-full right-0 flex flex-col gap-2 mt-3',
          itemAnimation: { x: 20 },
        };
      case 'bottom-left':
        return {
          initial: { opacity: 0, y: 20, x: -20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: 20, x: -20 },
          menuClassName: 'absolute bottom-full left-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: -20 },
        };
      case 'bottom-right':
        return {
          initial: { opacity: 0, y: 20, x: 20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: 20, x: 20 },
          menuClassName: 'absolute bottom-full right-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: 20 },
        };
      default:
        return {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 20 },
          menuClassName: 'absolute bottom-full right-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: 20 },
        };
    }
  };

  const animationValues = getAnimationValues();

  const links: MenuLink[] = [
    ...(chat.started
      ? [
          {
            type: 'link' as const,
            icon: <div className="i-ph:headset-light"></div>,
            title: 'Chat with Alpha Expert',
            url: 'https://calendly.com/dualite-discussion/30min?month=2025-05',
            delay: 0.1,
          },
        ]
      : []),
    {
      type: 'link' as const,
      icon: <div className="i-ph:video-light"></div>,
      title: 'Tutorials',
      url: 'https://www.youtube.com/playlist?list=PLcoySjNMrqYTSoW0QlaOWDSWk7IcrieIr',
      delay: 0.1,
    },
    {
      type: 'link' as const,
      icon: <div className="i-ph:book-open-light"></div>,
      title: 'Docs',
      url: 'https://alphadocs.dualite.dev',
      delay: 0.2,
    },
    {
      type: 'link' as const,
      icon: <div className="i-ph:clipboard-text-light"></div>,
      title: 'Update Logs',
      url: 'https://alphadocs.dualite.dev/update-logs',
      delay: 0.3,
    },
    {
      type: 'bug' as const,
      icon: <div className="i-ph:bug-light"></div>,
      title: 'Report an Issue',
      url: '#',
      delay: 0.4,
    },
    {
      type: 'feature' as const,
      icon: <div className="i-ph:lightbulb text-lg"></div>,
      title: 'Feature Request',
      url: '#',
      delay: 0.5,
    },
  ];

  return (
    <div className="z-50 inline-block" style={menuContainerStyle}>
      {/* Menu Items */}
      {isOpen && (
        <motion.div
          className={animationValues.menuClassName}
          initial={animationValues.initial}
          animate={animationValues.animate}
          exit={animationValues.exit}
          style={
            withBg
              ? {
                  backgroundColor: 'rgba(20, 20, 20, 1)',
                  padding: '12px',
                  borderRadius: '12px',
                }
              : {}
          }
        >
          {links.map((link, index) => (
            <motion.div
              onClick={() => handleLinkClick(link)}
              key={index}
              className="cursor-pointer flex items-center gap-2 bg-zinc-800/90 hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border border-zinc-700/50 shadow-md whitespace-nowrap"
              initial={{ opacity: 0, ...animationValues.itemAnimation }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: link.delay || index * 0.1 }}
            >
              {link.icon}
              {link.title}
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Help Button */}
      <motion.button
        ref={buttonRef}
        onClick={toggleMenu}
        className={buttonClassName}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={isOpen ? { rotate: 90 } : { rotate: 0 }}
      >
        {isOpen ? <div className="i-ph:x-bold text-xl" /> : <div className="i-ph:question text-2xl" />}
      </motion.button>
    </div>
  );
};

export default HelpMenu;
